import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Object_Properties() {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🏷️ JavaScript Object Properties</h1>

      <p className="tutorial-intro">
        In JavaScript, <strong>object properties</strong> are key–value pairs
        that define the characteristics or data of an object. Each property
        consists of a name (key) and a value that can be any data type — string,
        number, function, or even another object.
      </p>

      {/* 🧠 Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Object properties are written as{" "}
            <code className="highlight_code">key: value</code> pairs.
          </li>
          <li>
            Property names can be strings or symbols; values can be any type.
          </li>
          <li>
            You can access properties using dot notation ({" "}
            <code className="highlight_code">obj.name</code>) or bracket
            notation ( <code className="highlight_code">obj["name"]</code>).
          </li>
          <li>
            The <code className="highlight_code">delete</code> operator removes
            a property from an object.
          </li>
          <li>
            Use <code className="highlight_code">Object.keys()</code>,{" "}
            <code className="highlight_code">Object.values()</code>, and{" "}
            <code className="highlight_code">Object.entries()</code> to iterate
            properties.
          </li>
        </ul>
      </div>

      {/* 1️⃣ Creating & Accessing Properties */}
      <div className="tutorial-section">
        <h3>1️⃣ Creating and Accessing Properties</h3>
        <p>
          You can create objects with properties and access them using dot or
          bracket notation.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const person = {
  name: "Alice",
  age: 25,
  city: "Paris"
};

console.log(person.name);     // Dot notation
console.log(person["age"]);   // Bracket notation`}
          />
          <TryItButton
            code={`const person={name:"Alice",age:25,city:"Paris"};console.log(person.name);console.log(person["age"]);`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            {`Alice
25`}
          </pre>
        </OutputBox>
      </div>

      {/* 2️⃣ Adding & Deleting Properties */}
      <div className="tutorial-section">
        <h3>2️⃣ Adding and Deleting Properties</h3>
        <p>
          You can dynamically add or remove properties from an object using dot
          notation or the <code className="highlight_code">delete</code>{" "}
          operator.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const car = { brand: "Tesla" };

car.model = "Model S";  // Add a property
console.log(car);

delete car.brand;        // Delete a property
console.log(car);`}
          />
          <TryItButton
            code={`const car={brand:"Tesla"};car.model="Model S";console.log(car);delete car.brand;console.log(car);`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            {`{ brand: 'Tesla', model: 'Model S' }
{ model: 'Model S' }`}
          </pre>
        </OutputBox>
      </div>

      {/* 3️⃣ Property Shorthand & Computed Names */}
      <div className="tutorial-section">
        <h3>3️⃣ Property Shorthand & Computed Property Names</h3>
        <p>
          ES6 allows shorthand syntax for properties and computed keys using
          square brackets.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const name = "Bob";
const age = 30;
const key = "country";

const user = {
  name,         // Shorthand for name: name
  age,          // Shorthand for age: age
  [key]: "USA"  // Computed property name
};

console.log(user);`}
          />
          <TryItButton
            code={`const name="Bob";const age=30;const key="country";const user={name,age,[key]:"USA"};console.log(user);`}
          />
        </div>
        <OutputBox>
          Output: {"{ name: 'Bob', age: 30, country: 'USA' }"}
        </OutputBox>
      </div>

      {/* 4️⃣ Iterating Over Properties */}
      <div className="tutorial-section">
        <h3>4️⃣ Iterating Over Object Properties</h3>
        <p>
          Use <code className="highlight_code">Object.keys()</code>,{" "}
          <code className="highlight_code">Object.values()</code>, and{" "}
          <code className="highlight_code">Object.entries()</code> to loop
          through an object's properties.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const book = {
  title: "1984",
  author: "George Orwell",
  year: 1949
};

console.log(Object.keys(book));   // Property names
console.log(Object.values(book)); // Property values
console.log(Object.entries(book));// Key-value pairs`}
          />
          <TryItButton
            code={`const book={title:"1984",author:"George Orwell",year:1949};console.log(Object.keys(book));console.log(Object.values(book));console.log(Object.entries(book));`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            {`[ 'title', 'author', 'year' ]
[ '1984', 'George Orwell', 1949 ]
[ [ 'title', '1984' ], [ 'author', 'George Orwell' ], [ 'year', 1949 ] ]`}
          </pre>
        </OutputBox>
      </div>

      {/* 5️⃣ Property Descriptors */}
      <div className="tutorial-section">
        <h3>5️⃣ Property Descriptors (Advanced)</h3>
        <p>
          Every property has a descriptor object that defines attributes like{" "}
          <code className="highlight_code">writable</code>,{" "}
          <code className="highlight_code">enumerable</code>, and{" "}
          <code className="highlight_code">configurable</code>.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const obj = { x: 42 };
const descriptor = Object.getOwnPropertyDescriptor(obj, "x");

console.log(descriptor);`}
          />
          <TryItButton
            code={`const obj={x:42};const descriptor=Object.getOwnPropertyDescriptor(obj,"x");console.log(descriptor);`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            {`{ value: 42, writable: true, enumerable: true, configurable: true }`}
          </pre>
        </OutputBox>
      </div>

      {/* 🚀 Next Step */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Step</h2>
        <p>
          Learn about <strong>Object Methods</strong> next — like{" "}
          <code className="highlight_code">Object.assign()</code> and{" "}
          <code className="highlight_code">Object.freeze()</code> for copying or
          protecting properties.
        </p>
      </div>
    </div>
  );
}
